// The poster catalogue — every A4 poster design, in one place.
//
// Browser-side twin of HG-CC's `scripts/collateral/lib/poster-catalogue.mjs`.
// Same designs, same layouts, same geometry. Read that file before changing
// either one.
//
// Two documents draw from here:
//   - the activation pack, which takes the eight marked `pack: true`
//   - the poster book, which prints all of them, grouped into sections
//
// Adding a design adds it to the book with no other edit, and never changes
// the pack unless you mark it.
//
// Layout archetypes:
//   hero       headline, one line of support, big code
//   steps      three numbered steps, code below
//   stat       one huge number, code below
//   covers     four things the genie helps with, code below
//   quote      one line in speech marks
//   qrmax      almost nothing but the code, reads across a room
//   notice     staff-facing, on a tinted card, no marketing voice
//   a5pair     two half-size posters on one A4 sheet, cut down the middle

import { qrCentreMark, qrCentreMarkCss, qrForSize } from "./qr";
import {
  A4,
  TONES,
  brandLockup,
  escapeHtml,
  font,
  isOnColour,
  palette,
  poweredBy,
  type PackTone,
} from "./tokens";
import type { PackPagePart } from "./physical";
import { resolvePrintBrand } from "./printBrand";
import { heroTitleOf, type PackKit } from "@/types/activation/packKit";

export type PosterLayout =
  | "hero"
  | "steps"
  | "stat"
  | "covers"
  | "quote"
  | "qrmax"
  | "notice"
  | "a5pair";

export type PosterSection = "front" | "how" | "why" | "scan" | "staff";

export interface PosterSpec {
  id: string;
  section: PosterSection;
  layout: PosterLayout;
  tone: PackTone;
  /** Shown in the picker so a rep knows which design they are toggling off. */
  label: string;
  /** Where this sheet belongs on a wall. Prints in the book's contents. */
  use: string;
  /** In the activation pack as well as the book. */
  pack?: boolean;
  headline?: string;
  sub?: string;
  cta?: string;
  quote?: string;
  steps?: [string, string][];
  items?: string[];
  notes?: [string, string][];
  stat?: string;
  statUnit?: string;
}

export const POSTER_SECTIONS: { key: PosterSection; title: string; sub: string; tone: PackTone }[] = [
  {
    key: "front",
    title: "Front of house.",
    sub: "The first thing a customer sees. Door, window, reception, counter. Put one of these where somebody stands and waits.",
    tone: "dark",
  },
  {
    key: "how",
    title: "What it covers.",
    sub: "Nobody needs a QR code explained to them. What they do need is to know what the genie can actually help with, and that there is nothing to download.",
    tone: "primary",
  },
  {
    key: "why",
    title: "Why it is worth it.",
    sub: "One number, big enough to read on the way past. Use these where people are moving rather than waiting.",
    tone: "secondary",
  },
  {
    key: "scan",
    title: "Big code targets.",
    sub: "Almost no words. These are for a noticeboard at the far end of a hall, a corridor, a car park sign, or anywhere the reader is more than three metres away.",
    tone: "dark",
  },
  {
    key: "staff",
    title: "For the team.",
    sub: "Back of house. Staff room, till, workshop wall. These tell your own people what the genie is and what to say when somebody asks.",
    tone: "primary",
  },
];

/** Every design, filled from the kit's own words. */
export function posterCatalogue(kit: PackKit): PosterSpec[] {
  const who = escapeHtml(kit.shortName || kit.name);
  const helps = kit.helps;
  const genieName = escapeHtml(kit.genieName);

  return [
    // ── Front of house ───────────────────────────────────────────────────────
    // The hero is the default piece. Tim's structure, 2026-08-06: an
    // introduction, the categories the genie covers, then where the code lands.
    {
      id: "introducing", section: "front", layout: "covers", tone: "dark", pack: true,
      label: "Introducing the genie",
      // The genie's own resolved name, not a title built from the business
      // name. `genieDisplayName` has already deduped it, and it is the name the
      // customer picked in the platform — so the poster on the wall and the
      // heading in the app say the same thing.
      headline: `Introducing the<br>${escapeHtml(kit.genieName || heroTitleOf(kit.name))}.`,
      sub: "Here to help, any hour of any day, with;",
      items: helps,
      cta: kit.stickerLine,
      use: "The default poster. Reception, counter, workshop wall",
    },
    {
      id: "ask-anything", section: "front", layout: "hero", tone: "dark", pack: true,
      label: "Ask us anything",
      headline: "Ask us<br>anything.",
      sub: `Real answers about ${kit.subject}, in seconds, at any hour.`,
      cta: genieName,
      use: "Front door, reception, waiting area",
    },
    {
      id: "meet-the-genie", section: "front", layout: "hero", tone: "light", pack: true,
      label: "Meet the genie",
      headline: `Meet the<br>${who}<br>genie.`,
      sub: "It knows what we know, and it never goes home.",
      cta: genieName,
      use: "Counter, reception desk, first wall inside the door",
    },
    {
      id: "now-answering", section: "front", layout: "hero", tone: "primary", pack: true,
      label: "Now answering",
      headline: "Now answering<br>your questions.",
      sub: `${kit.name} answers ${kit.audience} 24 hours a day.`,
      cta: "Every day, every hour, in 50+ languages",
      use: "Window, facing out",
    },
    {
      id: "ask-a-friend", section: "front", layout: "hero", tone: "light", pack: true,
      label: "Ask it like a person",
      headline: "Ask it like<br>you would ask<br>a person.",
      sub: "No menus, no keywords, no phone tree. Just say what you need.",
      cta: genieName,
      use: "Anywhere somebody is queueing",
    },
    {
      id: "closed-not-closed", section: "front", layout: "hero", tone: "dark",
      label: "We are closed, the genie is not",
      headline: "We are closed.<br>The genie<br>is not.",
      sub: `Ask about ${kit.subject} at any hour and get an answer straight back.`,
      cta: "Open at 2am, and on Sundays",
      use: "Door glass, facing out, for after hours",
    },
    {
      id: "nobody-at-the-desk", section: "front", layout: "hero", tone: "light",
      label: "Nobody at the desk",
      headline: "Nobody at<br>the desk?<br>Ask anyway.",
      sub: "The genie answers while we are with someone else, on a break, or gone for the day.",
      cta: genieName,
      use: "Unstaffed reception, self-serve counter",
    },

    // ── How it works ─────────────────────────────────────────────────────────
    // The old "Not sure what to ask?" poster printed four sample questions. Tim's
    // rule: a printed example narrows the reader down to that one thing and
    // misfires when they wanted something else. Categories instead.
    {
      id: "what-it-covers", section: "how", layout: "covers", tone: "light", pack: true,
      label: "What the genie covers",
      headline: "What the<br>genie covers.",
      sub: "Ask about any of it, in your own words.",
      items: helps,
      cta: genieName,
      use: "Waiting area, where somebody has a minute to read",
    },
    {
      id: "no-app-needed", section: "how", layout: "steps", tone: "primary",
      label: "Nothing to download",
      headline: "Nothing<br>to download.",
      steps: [
        ["No app", "It opens in the browser you already have."],
        ["No account", "No email, no password, no sign up screen."],
        ["No cost", `Asking ${kit.shortName || kit.name} a question is free, the way it always was.`],
      ],
      // The one poster that still names the app, because this is the sheet
      // whose whole subject is "there is nothing to install". Everywhere else
      // the call to action is the customer's own.
      cta: "Or take the Help Genie app and carry it with you",
      use: "For the audience that assumes a QR means an app store",
    },
    {
      id: "type-or-talk", section: "how", layout: "quote", tone: "dark",
      label: "Type it like you would say it",
      quote: "Say it the way you would say it to us.",
      sub: "No keywords, no menu, no forms. The genie reads a question the way a person would, and answers the same way.",
      cta: genieName,
      use: "Counter, or beside a form nobody wants to fill in",
    },

    // ── Why it is worth scanning ─────────────────────────────────────────────
    {
      id: "zero-wait", section: "why", layout: "stat", tone: "dark", pack: true,
      label: "Zero wait time",
      stat: "Zero",
      statUnit: "wait time.",
      sub: `Every question about ${kit.subject}, answered the moment you ask it.`,
      cta: genieName,
      use: "Corridor, walkway, anywhere people move past",
    },
    {
      id: "own-language", section: "why", layout: "stat", tone: "secondary", pack: true,
      label: "50+ languages",
      stat: "50+",
      statUnit: "languages.",
      sub: "Ask in the language you think in. The answer comes back the same way.",
      cta: genieName,
      use: "Anywhere with visitors, tourists or a mixed community",
    },
    {
      id: "always-open", section: "why", layout: "stat", tone: "dark",
      label: "24/7 answers",
      stat: "24/7",
      statUnit: "answers.",
      sub: "Midnight, Sunday, a public holiday. The genie does not keep our hours.",
      cta: genieName,
      use: "Door, gate, after-hours entrance",
    },
    {
      id: "seconds", section: "why", layout: "stat", tone: "primary",
      label: "Seconds not days",
      stat: "Seconds",
      statUnit: "not days.",
      sub: "No hold music, no callback promise, no email that gets answered on Tuesday.",
      cta: genieName,
      use: "Beside a phone, a service desk, or an enquiry form",
    },
    {
      id: "one-scan", section: "why", layout: "stat", tone: "highlight",
      label: "One code",
      stat: "One",
      statUnit: "code.",
      sub: `Everything ${kit.shortName || kit.name} knows, in the thing already in your hand.`,
      cta: genieName,
      use: "High-traffic wall, lift lobby, stairwell",
    },

    // ── Big scan targets ─────────────────────────────────────────────────────
    {
      id: "big-qr", section: "scan", layout: "qrmax", tone: "light", pack: true,
      label: "Big QR, reads across a room",
      headline: escapeHtml(kit.stickerName),
      sub: `${kit.name}. Answers in seconds.`,
      use: "Noticeboard, read from across a room",
    },
    {
      id: "big-qr-dark", section: "scan", layout: "qrmax", tone: "dark",
      label: "Big QR, dark",
      headline: "Questions?",
      sub: `${kit.genieName}. Any hour, any day.`,
      use: "Dark wall, or where a white sheet disappears",
    },
    {
      id: "big-qr-primary", section: "scan", layout: "qrmax", tone: "primary",
      label: "Big QR, on your brand colour",
      headline: "Ask here.",
      sub: `${kit.name} answers 24 hours a day.`,
      use: "Car park, gate, outdoor sign holder",
    },
    {
      id: "a5-pair", section: "scan", layout: "a5pair", tone: "dark",
      label: "Two A5 posters on one sheet",
      headline: "Ask us anything.",
      sub: `${kit.shortName || kit.name}. Answers in seconds.`,
      use: "Small spaces, lift walls, toilet doors, desk fronts",
    },

    // ── For the team ─────────────────────────────────────────────────────────
    {
      id: "staff-what-it-is", section: "staff", layout: "notice", tone: "light",
      label: "Staff: what the genie is",
      headline: "What the genie is",
      sub: `${kit.name} has a Genie on the website and behind every QR code in the building. It answers questions about ${kit.subject} using what we have told it.`,
      notes: [
        ["It knows", "Our hours, prices, policies, process and the answers we give most often."],
        ["It does not know", "Anything we have not given it. If it is wrong, tell whoever owns the genie and it gets fixed the same day."],
        ["It never guesses a person", "When somebody needs a human, it takes their details and passes them to us."],
      ],
      cta: "See what a customer sees",
      use: "Staff room, back office",
    },
    {
      id: "staff-what-to-say", section: "staff", layout: "notice", tone: "light",
      label: "Staff: what to say",
      headline: "What to say when they ask",
      sub: "Somebody points at the poster and asks what it is. Any of these works.",
      notes: [
        ["The short version", '"Scan that and you can ask us anything, any time. It answers straight away."'],
        ["If they look unsure", '"Just open your camera and point it. No app, nothing to sign up for."'],
        ["If they are busy", '"Take a card. The code is on it, ask us later from home."'],
      ],
      cta: "Try it yourself first",
      use: "Till, service desk, workshop wall",
    },
    {
      id: "staff-tell-us", section: "staff", layout: "notice", tone: "light",
      label: "Staff: if it gets it wrong",
      headline: "If the genie gets it wrong",
      sub: "It is only as good as what we have given it, and that is fixable in minutes. A wrong answer nobody reports is the only real problem.",
      notes: [
        ["Screenshot it", "The question and the answer, both in frame."],
        ["Send it on", "To whoever owns the genie here. No form, no ticket."],
        ["It gets fixed", "Usually the same day, and every customer after that gets the right answer."],
      ],
      cta: "Check a question yourself",
      use: "Staff room, next to the roster",
    },
  ];
}

/**
 * The activation pack's eight, by id and in print order, with the label the
 * picker shows. The pack is the curated day-one document: it does not grow
 * every time the catalogue does.
 */
export const POSTER_CATALOGUE: { id: string; label: string }[] = posterCatalogue({
  name: "", shortName: "", subject: "", audience: "", footer: "", slug: "", url: "", stickerLine: "",
  helps: ["", "", "", ""], genieName: "", genieUseCase: "", stickerName: "",
  nameIsGenie: false, subLine: "", footerLine: null, placements: [],
  brand: resolvePrintBrand(null, ""),
})
  .filter((p) => p.pack)
  .map((p) => ({ id: p.id, label: p.label }));

/** The pack's designs, filled from the kit. Kept for the pack page's picker. */
export function posterSpecs(kit: PackKit): PosterSpec[] {
  return posterCatalogue(kit).filter((p) => p.pack);
}

// ─── Renderer ────────────────────────────────────────────────────────────────
export async function posterPage(kit: PackKit, spec: PosterSpec, n: number): Promise<PackPagePart> {
  if (spec.layout === "a5pair") return a5PairPage(kit, spec, n);

  const t = TONES[spec.tone];
  const light = !isOnColour(spec.tone);
  const muted = light ? palette.mutedFg : "rgba(255,255,255,0.86)";
  const notice = spec.layout === "notice";
  // The covers layout carries the most on one sheet: a headline that can run to
  // three lines, four category cards, the code, and a caption. Everything on it
  // steps down a size so a long business name cannot push the caption off the
  // page. Verified against the A3 sheet in HG-CC before porting.
  const covers = spec.layout === "covers";

  // Code size per archetype. These are the numbers the layout was proved at:
  // raising one without re-rendering is how a footer gets clipped.
  const qrMm =
    { qrmax: 120, steps: 58, questions: 54, quote: 66, notice: 44, hero: 78, stat: 74 }[
      spec.layout as Exclude<PosterLayout, "a5pair">
    ] ?? 74;
  const q = await qrForSize(kit.url, qrMm, { ink: kit.brand.inkDeep, label: `poster-${spec.id}` });

  // The QR always sits on a white card. A code printed straight onto navy still
  // decodes, but a phone locks on far faster with a real quiet zone around it,
  // and half the value of a poster is the person who scans it while walking.
  const qrCard = `<div class="qrcard"><div class="qrin">${q.svg}${qrCentreMark(q, qrMm, kit.brand)}</div></div>`;

  let body: string;
  if (spec.layout === "hero") {
    body = `
      <h1>${spec.headline}</h1>
      <p class="sub">${escapeHtml(spec.sub ?? "")}</p>
      ${qrCard}
      <p class="cta">${escapeHtml(spec.cta ?? "")}</p>`;
  } else if (spec.layout === "steps") {
    const items = (spec.steps ?? [])
      .map(
        (s, i) => `
      <li><span class="num">${i + 1}</span>
        <span class="stepbody"><b>${escapeHtml(s[0])}</b><em>${escapeHtml(s[1])}</em></span>
      </li>`,
      )
      .join("");
    body = `
      <h1>${spec.headline}</h1>
      <ol class="steps">${items}</ol>
      ${qrCard}
      <p class="cta">${escapeHtml(spec.cta ?? "")}</p>`;
  } else if (spec.layout === "stat") {
    body = `
      <div class="statwrap">
        <div class="stat">${escapeHtml(spec.stat ?? "")}</div>
        <div class="statunit">${escapeHtml(spec.statUnit ?? "")}</div>
      </div>
      <p class="sub">${escapeHtml(spec.sub ?? "")}</p>
      ${qrCard}
      <p class="cta">${escapeHtml(spec.cta ?? "")}</p>`;
  } else if (spec.layout === "covers") {
    const items = (spec.items ?? []).slice(0, 4).map((x) => `<li>${escapeHtml(x)}</li>`).join("");
    body = `
      <h1>${spec.headline}</h1>
      <p class="sub tight">${escapeHtml(spec.sub ?? "")}</p>
      <ul class="qlist">${items}</ul>
      ${qrCard}
      <p class="cta">${escapeHtml(spec.cta ?? "")}</p>`;
  } else if (spec.layout === "quote") {
    body = `
      <div class="bigquote">${escapeHtml(spec.quote ?? "")}</div>
      <p class="sub">${escapeHtml(spec.sub ?? "")}</p>
      ${qrCard}
      <p class="cta">${escapeHtml(spec.cta ?? "")}</p>`;
  } else if (spec.layout === "notice") {
    const items = (spec.notes ?? [])
      .map((x) => `<li><b>${escapeHtml(x[0])}</b><span>${escapeHtml(x[1])}</span></li>`)
      .join("");
    body = `
      <h1 class="notice">${escapeHtml(spec.headline ?? "")}</h1>
      <p class="sub tight left">${escapeHtml(spec.sub ?? "")}</p>
      <ul class="notes">${items}</ul>
      <div class="noticefoot">
        ${qrCard}
        <p class="cta">${escapeHtml(spec.cta ?? "")}</p>
      </div>`;
  } else {
    // qrmax: designed to be read from the far side of a room. Type is minimal
    // on purpose. This is the noticeboard sheet.
    body = `
      <h1 class="huge">${escapeHtml(spec.headline ?? "")}</h1>
      ${qrCard}
      <p class="sub big">${escapeHtml(spec.sub ?? "")}</p>`;
  }

  const css = `
    #p${n} { background: ${t.bg}; color: ${t.fg};
      display: flex; flex-direction: column; align-items: ${notice ? "stretch" : "center"};
      text-align: ${notice ? "left" : "center"};
      padding: ${notice ? "18mm 20mm 14mm" : "20mm 18mm 16mm"}; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: ${A4.w}mm; height: 3.5mm; background: ${t.accent}; }
    #p${n} .lockup { margin-bottom: ${notice ? 9 : 11}mm; }
    #p${n} h1 { font-family: ${font.display}; font-size: ${covers ? 12.5 : 17}mm; line-height: 1.02;
      font-weight: 800; margin: 0 0 ${covers ? 5 : 6}mm; letter-spacing: -0.015em; }
    #p${n} h1.huge { font-size: 24mm; margin-bottom: 10mm; }
    #p${n} h1.notice { font-size: 12mm; margin-bottom: 4mm; }
    #p${n} .sub { font-size: 5.2mm; font-weight: 500; margin: 0 0 ${covers ? 6 : 11}mm;
      max-width: 150mm; line-height: 1.35; color: ${muted}; }
    #p${n} .sub.tight { margin-bottom: 7mm; }
    #p${n} .sub.left { max-width: none; }
    #p${n} .sub.big { font-size: 6.4mm; font-weight: 600; margin-top: 10mm; margin-bottom: 0;
      color: ${light ? palette.ink : "#FFFFFF"}; }
    #p${n} .qrcard { background: #FFFFFF; border-radius: 6mm; padding: ${notice ? 4 : 7}mm;
      ${light ? `border: 1.2mm solid ${palette.primary};` : ""}
      ${notice ? "flex: 0 0 auto;" : "margin-bottom: 9mm;"} }
    #p${n} .qrin svg { width: ${qrMm}mm; height: ${qrMm}mm; }
    ${qrCentreMarkCss(`#p${n}`)}
    #p${n} .cta { font-family: ${font.display}; font-weight: 700;
      font-size: ${notice ? 5.6 : covers ? 4.8 : 7.6}mm; line-height: 1.3;
      max-width: ${covers ? 150 : 175}mm;
      margin: 0 0 ${notice ? 0 : 4}mm; color: ${t.cta}; }

    /* steps */
    #p${n} .steps { list-style: none; margin: 0 0 11mm; padding: 0; text-align: left; max-width: 150mm; }
    #p${n} .steps li { display: flex; align-items: flex-start; gap: 6mm; margin-bottom: 7mm; }
    #p${n} .num { flex: 0 0 auto; width: 12mm; height: 12mm; border-radius: 50%;
      background: ${light ? palette.primary : "#FFFFFF"}; color: ${light ? "#FFFFFF" : palette.inkDeep};
      font-family: ${font.display}; font-weight: 800; font-size: 6.4mm;
      display: flex; align-items: center; justify-content: center; }
    #p${n} .stepbody { display: block; padding-top: 0.8mm; }
    #p${n} .stepbody b { display: block; font-size: 5.6mm; font-weight: 700;
      color: ${light ? palette.ink : "#FFFFFF"}; margin-bottom: 1.4mm; }
    #p${n} .stepbody em { display: block; font-size: 4.3mm; font-style: normal;
      color: ${muted}; line-height: 1.35; }

    /* stat */
    #p${n} .statwrap { margin-bottom: 6mm; }
    #p${n} .stat { font-family: ${font.display}; font-size: 40mm; font-weight: 800;
      line-height: 0.9; letter-spacing: -0.03em; }
    #p${n} .statunit { font-family: ${font.display}; font-size: 13mm; font-weight: 700; line-height: 1.1; }

    /* covers */
    #p${n} .qlist { list-style: none; margin: 0 0 7mm; padding: 0; max-width: 152mm; width: 100%; }
    #p${n} .qlist li { font-size: 4.5mm; font-weight: 600; line-height: 1.25; text-align: left;
      color: ${light ? palette.ink : "#FFFFFF"};
      background: ${light ? palette.pageTint : "rgba(255,255,255,0.12)"};
      border-left: 1.4mm solid ${light ? palette.secondary : palette.highlight};
      border-radius: 0 3mm 3mm 0; padding: 3.2mm 5.5mm; margin-bottom: 2.8mm; }

    /* quote */
    #p${n} .bigquote { font-family: ${font.display}; font-size: 13mm; font-weight: 800;
      line-height: 1.1; letter-spacing: -0.015em; margin: 0 0 8mm; max-width: 165mm; }

    /* notice */
    #p${n} .notes { list-style: none; margin: 0 0 8mm; padding: 0; }
    #p${n} .notes li { padding: 4mm 0; border-top: 0.3mm solid ${palette.border}; }
    #p${n} .notes li b { display: block; font-family: ${font.display}; font-size: 5.4mm;
      font-weight: 700; color: ${palette.ink}; margin-bottom: 1.6mm; }
    #p${n} .notes li span { display: block; font-size: 4mm; color: ${palette.mutedFg}; line-height: 1.4; }
    #p${n} .noticefoot { display: flex; align-items: center; gap: 7mm; margin-top: auto;
      background: ${palette.pageTint}; border: 0.3mm solid ${palette.border};
      border-radius: 4mm; padding: 6mm; }

    #p${n} .foot { margin-top: auto; display: flex; flex-direction: column; gap: 2mm;
      align-items: ${notice ? "flex-start" : "center"}; ${notice ? "padding-top: 6mm;" : ""} }
    #p${n} .foot .url { font-size: 4.8mm; font-weight: 600; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    ${brandLockup(kit.brand, 13, t.mark === "light" ? "light" : "dark")}
    ${body}
    <div class="foot">
      <span class="url">${escapeHtml(kit.footer)}</span>
      ${poweredBy(light ? "light" : "dark")}
    </div>
    <div class="pagenum ${light ? "" : "oncolour"}">${n}</div>
  </div>`;

  return { html, css, qr: { label: `poster ${spec.label}`, sizeMm: qrMm, qr: q } };
}

/**
 * Two half-size posters on one A4 sheet, cut across the middle. A business with
 * six small noticeboards does not want six A4 sheets.
 */
async function a5PairPage(kit: PackKit, spec: PosterSpec, n: number): Promise<PackPagePart> {
  const panelH = A4.h / 2;
  const q = await qrForSize(kit.url, 52, { ink: kit.brand.inkDeep, label: `poster-${spec.id}` });

  const panel = `
    <div class="pinner">
      ${brandLockup(kit.brand, 9, "dark")}
      <h2>${escapeHtml(spec.headline ?? "")}</h2>
      <div class="row">
        <div class="qrcard">${q.svg}</div>
        <div class="copy">
          <p class="cta">${escapeHtml(kit.genieName)}</p>
          <p class="q">${escapeHtml(spec.sub ?? "")}</p>
          <p class="url">${escapeHtml(kit.footer)}</p>
        </div>
      </div>
    </div>`;

  const css = `
    #p${n} .panel { position: absolute; left: 0; width: ${A4.w}mm; height: ${panelH}mm;
      background: ${palette.inkDeep}; color: #FFFFFF; overflow: hidden; }
    #p${n} .panel.top { top: 0; }
    #p${n} .panel.bottom { top: ${panelH}mm; }
    #p${n} .pinner { position: absolute; inset: 0; padding: 14mm 16mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
    #p${n} .pinner .lockup { margin-bottom: 6mm; }
    #p${n} h2 { font-family: ${font.display}; font-size: 11mm; font-weight: 800; margin: 0 0 7mm; line-height: 1.05; }
    #p${n} .row { display: flex; align-items: center; gap: 9mm; text-align: left; }
    #p${n} .qrcard { background: #FFFFFF; border-radius: 3.5mm; padding: 4.5mm; flex: 0 0 auto; }
    #p${n} .qrcard svg { width: 52mm; height: 52mm; }
    #p${n} .copy { max-width: 78mm; }
    #p${n} .cta { font-family: ${font.display}; font-weight: 700; font-size: 6.2mm;
      color: ${palette.highlight}; margin: 0 0 3mm; line-height: 1.15; }
    #p${n} .q { font-size: 4.1mm; color: rgba(255,255,255,0.85); margin: 0 0 4mm; line-height: 1.35; }
    #p${n} .url { font-size: 4.2mm; font-weight: 600; margin: 0; }
    #p${n} .cutline { position: absolute; left: 0; top: ${panelH}mm; width: ${A4.w}mm; height: 0;
      border-top: 0.25mm dashed rgba(255,255,255,0.55); }
    #p${n} .cutlabel { position: absolute; left: 5mm; top: ${panelH - 3.4}mm;
      font-size: 2.7mm; color: rgba(255,255,255,0.6); letter-spacing: 0.1em; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="panel top">${panel}</div>
    <div class="panel bottom">${panel}</div>
    <div class="cutline"></div>
    <div class="cutlabel">CUT HERE</div>
    <div class="pagenum oncolour">${n}</div>
  </div>`;

  return { html, css, qr: { label: "A5 poster", sizeMm: 52, qr: q } };
}
